/**
 * useLakehouses Hook
 *
 * React Query hooks for lakehouse catalog data with filtering and pagination.
 * Implements the v2.0.0 catalog features (FR-201, FR-202).
 *
 * Requirements:
 * - FR-201: View all lakehouses with protection tier
 * - FR-202: Filter by tier, environment, compliance
 * - FR-203: Assign protection tier
 * - FR-204: Update compliance tags
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import apiClient from '../services/api';

// Types
export type ProtectionTier = 'Gold' | 'Silver' | 'Bronze';
export type Environment = 'prod' | 'non-prod';

export interface LakehouseWithWorkspace {
  id: string;
  workspaceId: string;
  policyId?: string | null;
  fabricLakehouseId?: string;
  lakehouseName: string;
  environment: Environment;
  protectionTier: ProtectionTier;
  protectionTierSource: 'Auto' | 'Manual';
  complianceTags: string[];
  complianceTagSource: 'Auto-Detected' | 'Manual';
  sizeGb?: number | null;
  sizeBytes?: number | null;
  tableCount?: number | null;
  /** Spec 286 T159 item 4: set when the last collection did not size the lakehouse; its size is then old. */
  sizeMeasurementError?: string | null;
  sizeLastRecordedGb?: number | null;
  sizeLastRecordedOn?: string | null;
  description?: string | null;
  offsiteEnabled: boolean;
  archiveEnabled: boolean;
  lastBackupAt?: string | null;
  reviewRequired: boolean;
  enabled: boolean;
  createdAt: string;
  // Workspace join fields
  workspaceDisplayName: string;
  fabricWorkspaceId: string;
  policyName?: string;
}

export interface LakehouseCatalogFilters {
  workspaceId?: string;
  protectionTier?: ProtectionTier;
  environment?: Environment;
  complianceTag?: string;
  enabled?: boolean;
  /** Server-side filter on the backup_enabled column (query param the API actually reads). */
  is_backup_enabled?: boolean;
  reviewRequired?: boolean;
  search?: string;
}

export interface CatalogPagination {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CatalogResponse {
  data: LakehouseWithWorkspace[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface CatalogStats {
  total: number;
  byTier: Record<ProtectionTier, number>;
  byEnvironment: Record<string, number>;
  reviewRequired: number;
  unprotected: number;
  /** Total backup size (GB) across the active lakehouse population. */
  totalSizeGb?: number;
  /** Lakehouses the last collection did not size; the total counts their last known size (T159 item 32). */
  sizeNotMeasuredCount?: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

// Query keys
export const lakehouseQueryKeys = {
  all: ['lakehouses'] as const,
  catalog: (filters: LakehouseCatalogFilters, pagination: CatalogPagination) =>
    [...lakehouseQueryKeys.all, 'catalog', filters, pagination] as const,
  catalogStats: () => [...lakehouseQueryKeys.all, 'catalog-stats'] as const,
  detail: (id: string) => [...lakehouseQueryKeys.all, 'detail', id] as const,
  reviewRequired: () => [...lakehouseQueryKeys.all, 'review-required'] as const,
};

// Fetch functions
async function fetchCatalog(
  filters: LakehouseCatalogFilters,
  pagination: CatalogPagination
): Promise<CatalogResponse> {
  const params: Record<string, string | number | boolean | undefined> = {
    page: pagination.page,
    pageSize: pagination.pageSize,
    sortBy: pagination.sortBy,
    sortOrder: pagination.sortOrder,
    ...filters,
  };

  // Remove undefined values
  Object.keys(params).forEach((key) => {
    if (params[key] === undefined) {
      delete params[key];
    }
  });

  const response = await apiClient.get<ApiResponse<LakehouseWithWorkspace[]>>(
    '/api/v1/lakehouses',
    params
  );

  return {
    data: (response as ApiResponse<LakehouseWithWorkspace[]>).data,
    pagination: (response as ApiResponse<LakehouseWithWorkspace[]>).pagination!,
  };
}

async function fetchCatalogStats(): Promise<CatalogStats> {
  const response = await apiClient.get<ApiResponse<CatalogStats>>('/api/v1/lakehouses/stats');
  return (response as ApiResponse<CatalogStats>).data;
}

async function fetchLakehouseDetail(id: string): Promise<LakehouseWithWorkspace> {
  const response = await apiClient.get<ApiResponse<LakehouseWithWorkspace>>(
    `/api/v1/lakehouses/${id}`
  );
  return (response as ApiResponse<LakehouseWithWorkspace>).data;
}

async function assignTier(
  id: string,
  tier: ProtectionTier,
  policyId?: string | null
): Promise<LakehouseWithWorkspace> {
  const response = await apiClient.patch<ApiResponse<LakehouseWithWorkspace>>(
    `/api/v1/lakehouses/${id}/tier`,
    { protectionTier: tier, policyId }
  );
  return (response as ApiResponse<LakehouseWithWorkspace>).data;
}

async function updateComplianceTags(id: string, tags: string[]): Promise<LakehouseWithWorkspace> {
  const response = await apiClient.patch<ApiResponse<LakehouseWithWorkspace>>(
    `/api/v1/lakehouses/${id}/compliance-tags`,
    { tags }
  );
  return (response as ApiResponse<LakehouseWithWorkspace>).data;
}

async function bulkAssignTier(
  ids: string[],
  tier: ProtectionTier,
  policyId?: string | null
): Promise<{ success: number; failed: number; errors: Array<{ id: string; error: string }> }> {
  const response = await apiClient.post<
    ApiResponse<{ success: number; failed: number; errors: Array<{ id: string; error: string }> }>
  >('/api/v1/lakehouses/bulk/tier', { ids, protectionTier: tier, policyId });
  return (
    response as ApiResponse<{
      success: number;
      failed: number;
      errors: Array<{ id: string; error: string }>;
    }>
  ).data;
}

// Hooks

/**
 * Hook to fetch lakehouse catalog with filters and pagination
 */
export function useLakehouseCatalog(
  filters: LakehouseCatalogFilters = {},
  pagination: CatalogPagination = { page: 1, pageSize: 20 },
  options?: { enabled?: boolean }
): UseQueryResult<CatalogResponse> {
  return useQuery({
    queryKey: lakehouseQueryKeys.catalog(filters, pagination),
    queryFn: () => fetchCatalog(filters, pagination),
    staleTime: 30 * 1000, // 30 seconds
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch catalog statistics
 */
export function useCatalogStats(options?: { enabled?: boolean }): UseQueryResult<CatalogStats> {
  return useQuery({
    queryKey: lakehouseQueryKeys.catalogStats(),
    queryFn: fetchCatalogStats,
    staleTime: 60 * 1000, // 60 seconds
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch a single lakehouse detail
 */
export function useLakehouseDetail(
  id: string,
  options?: { enabled?: boolean }
): UseQueryResult<LakehouseWithWorkspace> {
  return useQuery({
    queryKey: lakehouseQueryKeys.detail(id),
    queryFn: () => fetchLakehouseDetail(id),
    staleTime: 30 * 1000,
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/**
 * Hook to assign protection tier
 */
export function useAssignTier(): UseMutationResult<
  LakehouseWithWorkspace,
  Error,
  { id: string; tier: ProtectionTier; policyId?: string | null }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, tier, policyId }) => assignTier(id, tier, policyId),
    onSuccess: () => {
      // Invalidate catalog queries
      queryClient.invalidateQueries({ queryKey: lakehouseQueryKeys.all });
    },
  });
}

/**
 * Hook to update compliance tags
 */
export function useUpdateComplianceTags(): UseMutationResult<
  LakehouseWithWorkspace,
  Error,
  { id: string; tags: string[] }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, tags }) => updateComplianceTags(id, tags),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lakehouseQueryKeys.all });
    },
  });
}

/**
 * Hook for bulk tier assignment
 */
export function useBulkAssignTier(): UseMutationResult<
  { success: number; failed: number; errors: Array<{ id: string; error: string }> },
  Error,
  { ids: string[]; tier: ProtectionTier; policyId?: string | null }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, tier, policyId }) => bulkAssignTier(ids, tier, policyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lakehouseQueryKeys.all });
    },
  });
}

/**
 * Combined hook for catalog page state
 */
export function useCatalogData(
  filters: LakehouseCatalogFilters = {},
  pagination: CatalogPagination = { page: 1, pageSize: 20 }
) {
  const catalog = useLakehouseCatalog(filters, pagination);
  const stats = useCatalogStats();

  return {
    catalog,
    stats,
    isLoading: catalog.isLoading || stats.isLoading,
    isError: catalog.isError || stats.isError,
    refetch: async () => {
      await Promise.all([catalog.refetch(), stats.refetch()]);
    },
  };
}

export default useLakehouseCatalog;
